import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  SettingsCartoAI,
  getSettingsCartoAI,
  testConnection,
  saveSettingsCartoAI,
  toggleSettingsCartoAI,
  updateSettingsCartoAI,
  fetchAvailableModelsByProvider,
  FetchModelsRequest,
  fetchAvailableModels,
  AvailableModel,
  AgentModelProvider
} from '../api/settingsCartoAI'

const SETTINGS_CARTO_AI_QUERY_KEY = 'settings-carto-ai'
const AVAILABLE_MODELS_QUERY_KEY = 'available-models'

export function useSettingsCartoAI() {
  const queryClient = useQueryClient()

  // Get current AI configuration
  const {
    data: settingsCartoAI,
    isLoading: isLoadingConfig,
    error: configError
  } = useQuery<SettingsCartoAI | null>({
    queryKey: [SETTINGS_CARTO_AI_QUERY_KEY],
    queryFn: getSettingsCartoAI
  })

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: testConnection,
    onError: (error) => {
      console.error('Connection test failed:', error)
    }
  })

  // Save AI configuration mutation
  const saveConfigMutation = useMutation({
    mutationFn: saveSettingsCartoAI,
    onSuccess: () => {
      // Invalidate and refetch the AI config
      queryClient.invalidateQueries([SETTINGS_CARTO_AI_QUERY_KEY])
    },
    onError: (error) => {
      console.error('Failed to save AI config:', error)
    }
  })

  const toggleConfigMutation = useMutation({
    mutationFn: toggleSettingsCartoAI,
    onSuccess: () => {
      queryClient.invalidateQueries([SETTINGS_CARTO_AI_QUERY_KEY])
    }
  })

  const deleteConfigMutation = useMutation({
    mutationFn: updateSettingsCartoAI,
    onSuccess: () => {
      queryClient.invalidateQueries([SETTINGS_CARTO_AI_QUERY_KEY])
    }
  })

  return {
    // Data
    settingsCartoAI,
    isLoadingConfig,
    configError,

    // Test connection
    testConnection: testConnectionMutation.mutate,
    isTestingConnection: testConnectionMutation.isLoading,
    connectionTestResult: testConnectionMutation.data,
    connectionTestError: testConnectionMutation.error,

    // Save configuration
    saveConfig: saveConfigMutation.mutate,
    isSavingConfig: saveConfigMutation.isLoading,
    saveConfigError: saveConfigMutation.error,

    // Delete configuration
    deleteConfig: deleteConfigMutation.mutate,
    isDeletingConfig: deleteConfigMutation.isLoading,
    deleteConfigError: deleteConfigMutation.error,

    // Toggle configuration
    toggleConfig: toggleConfigMutation.mutate,
    isTogglingConfig: toggleConfigMutation.isLoading,
    toggleConfigError: toggleConfigMutation.error,

    // Reset functions
    resetConnectionTest: testConnectionMutation.reset,
    resetSaveConfig: saveConfigMutation.reset
  }
}

export const useFetchAvailableModelsByProvider = () => {
  const queryClient = useQueryClient()

  const fetchAvailableModelsMutation = useMutation(
    (variables: { provider: AgentModelProvider; request: FetchModelsRequest }) =>
      fetchAvailableModelsByProvider(variables.provider, variables.request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SETTINGS_CARTO_AI_QUERY_KEY])
      }
    }
  )

  return fetchAvailableModelsMutation
}

export const useFetchAvailableModels = () => {
  return useQuery<AvailableModel[]>({
    queryKey: [AVAILABLE_MODELS_QUERY_KEY],
    queryFn: fetchAvailableModels
  })
}
